import {ScrollTrigger} from 'gsap/ScrollTrigger';
import type {Gsoa_ActionOffers_ActionOffer as ActionOffer} from '~/openapi';

const VISIBILITY_THRESHOLD_SECONDS = 3;
const INTERSECTION_THRESHOLDS = [0.1, 0.2];
const VISIBILITY_RATIO_HIGH = 0.2;
const VISIBILITY_RATIO_LOW = 0.1;

export const useLeafletScrollTriggers = (params: {
	viewportElements: Ref<HTMLElement[]>;
	activeLeafletPage: Ref<number>;
	scrollTriggerIsLoading: Ref<boolean>;
	leafletsTotalNumberOfPages: Ref<number> | ReturnType<typeof computed<number>>;
	leaflet: Ref<ActionOffer | null>;
	trackPageShowtime: (pageNumber: number, totalTime: number, count: number, totalPages: number, imageAsset?: string) => void;
	computerToHumanIndexing: (index: number) => number;
	humanToComputerIndexing: (pageNumber: number) => number;
}) => {
	const {
		viewportElements,
		activeLeafletPage,
		scrollTriggerIsLoading,
		leafletsTotalNumberOfPages,
		leaflet,
		trackPageShowtime,
		computerToHumanIndexing,
		humanToComputerIndexing,
	} = params;

	function setActiveLeafletPage(index: number) {
		activeLeafletPage.value = index;
	}

	function setupPageScrollTriggers() {
		const scrollTrigger = viewportElements.value.map((trigger, index) => {
			const onEnter = () => setActiveLeafletPage(computerToHumanIndexing(index));

			return ScrollTrigger.create({
				trigger,
				start: 'top 50%',
				end: 'bottom 50%',
				onEnter,
				onEnterBack: onEnter,
			});
		});
		scrollTriggerIsLoading.value = true;
		return scrollTrigger;
	}

	function setupListVisibilityTriggers(): Array<() => void> {
		let count = 0;
		return viewportElements.value.map((element, index) => {
			let startTime: number | null = null;
			let totalTime = 0;

			function onEnter() {
				startTime ??= Date.now();
			}

			function onLeave() {
				if (startTime === null) {
					return;
				}

				const endTime = Date.now();
				totalTime += (endTime - startTime) / 1000;
				startTime = null;

				if (totalTime >= VISIBILITY_THRESHOLD_SECONDS) {
					count++;
					trackPageShowtime(
						index + 1,
						Math.floor(totalTime),
						count,
						leafletsTotalNumberOfPages.value,
						leaflet.value?.storeDocuments?.[0]?.jpgAssets?.[humanToComputerIndexing(activeLeafletPage.value)],
					);
					totalTime = 0;
				}
			}

			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.intersectionRatio >= VISIBILITY_RATIO_HIGH) {
						onEnter();
					} else if (entry.intersectionRatio <= VISIBILITY_RATIO_LOW) {
						onLeave();
					}
				},
				{threshold: INTERSECTION_THRESHOLDS},
			);

			observer.observe(element);

			return () => {
				onLeave();
				observer.disconnect();
			};
		});
	}

	return {
		setupPageScrollTriggers,
		setupListVisibilityTriggers,
	};
};
